import { Box, Button, Paper, Stack, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { FC, memo, useCallback, useContext, useEffect, useReducer, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import LicenseContext from 'src/contexts/LicenseContext';
import { OutsourcingContractPdfPreviewRequestEntity, OutsourcingContractSetting, OutsourcingContractSettingChange } from 'src/entities/OutsourcingContractPdf.entity';
import { useMutationOutsourcingContractPdf } from 'src/hooks/useOutsourcingContractPdf.request';
import { queryKey, useMutationOutsourcingContractSetting, useOutsourcingContractSettingQuery } from 'src/hooks/useOutsourcingContractSetting.request';
import { useQueryCompany } from 'src/hooks/useQueryCompany';
import stringUtil from 'src/utils/string.util';

import DebouncedTextField from './DebouncedTextFieldProps';

function useFlash(value: string | File, delay = 2000): boolean {
  const [flash, setFlash] = useState<boolean>(false);
  useEffect(() => {
    setFlash(true);
    const timer: number = window.setTimeout(() => { setFlash(false); }, delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return flash;
}

type Action = { type: 'SET_FIELD', key: keyof OutsourcingContractSetting, value: string | File };

const formReducer = (state: OutsourcingContractSetting, action: Action): OutsourcingContractSetting => {
  switch (action.type) {
    case 'SET_FIELD':
      return { ...state, [action.key]: action.value };
    default:
      return state;
  }
};

const OutsourcingContractSettingsPresenter: FC = memo(() => {
  const licenseContext = useContext(LicenseContext);
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const { data: company } = useQueryCompany(licenseContext.config?.company_id);
  const { data: setting, isLoading: settingIsLoading } = useOutsourcingContractSettingQuery(
    licenseContext.config?.selected_company_id || licenseContext.config?.company_id,
    true
  );
  const { create, update } = useMutationOutsourcingContractSetting();
  const { create: createPreview } = useMutationOutsourcingContractPdf();
  const [previewHtml, setPreviewHtml] = useState<string>('');
  const [outsourcingContractSettingChange, setOutsourcingContractSettingChange] = useState<OutsourcingContractSettingChange>({
    flashIntroduction: false,
    flashAboutUnitPrice: false,
    flashPaymentTerm: false,
    flashCompanyName: false,
    flashCompanyAddress: false,
    flashCompanyPhoneNo: false,
    flashCompanyFaxNo: false,
    flashFreeTextOneTitle: false,
    flashFreeTextOneContent: false,
    flashFreeTextTwoTitle: false,
    flashFreeTextTwoContent: false,
    flashFreeTextThreeContent: false,
    flashFreeTextFourContent: false,
    flashImageFile: false,
    flashBranchName: false,
    flashContactPerson: false,
  });
  const [contentHeight, setContentHeight] = useState<number>(0);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const leftStackRef = useRef<HTMLDivElement>(null);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [shouldGeneratePreview, setShouldGeneratePreview] = useState(false);

  const initialFormState: OutsourcingContractSetting = {
    companyId: licenseContext.config?.selected_company_id || licenseContext.config?.company_id,
    introduction: '',
    aboutUnitPrice: '',
    paymentTerm: '',
    companyName: company?.company_name || '',
    companyAddress: company?.address || '',
    companyPhoneNo: company?.phone_number || '',
    companyFaxNo: company?.phone_number || '',
    freeTextOneTitle: '',
    freeTextOneContent: '',
    freeTextTwoTitle: '',
    freeTextTwoContent: '',
    freeTextThreeContent: '',
    freeTextFourContent: '',
    imageFile: null,
    imageUrl: null,
    branchName: '',
    contactPerson: '',
  };

  const [formState, dispatch] = useReducer(formReducer, initialFormState);
  const [debouncedState, dispatchDebounced] = useReducer(formReducer, initialFormState);

  useEffect(() => {
    if (settingIsLoading) return;
    if (!setting) return;

    Object.keys(setting).forEach((key: keyof OutsourcingContractSetting) => {
      dispatch({ type: 'SET_FIELD', key, value: setting[key] as string });
      dispatchDebounced({ type: 'SET_FIELD', key, value: setting[key] as string });
    });
  }, [setting, settingIsLoading]);

  type InputFieldMeta = {
    key: keyof OutsourcingContractSetting;
    label: string;
    rows?: number;
    minRows?: number;
  };

  const inputFields: InputFieldMeta[] = [
    {
      key: 'companyName',
      label: '会社名',
      rows: 1,
    },
    {
      key: 'branchName',
      label: '支社名',
      rows: 1,
    },
    {
      key: 'contactPerson',
      label: '担当者',
      rows: 1,
    },
    {
      key: 'companyAddress',
      label: '住所',
      rows: 1,
    },
    {
      key: 'companyPhoneNo',
      label: '電話番号',
      rows: 1,
    },
    {
      key: 'companyFaxNo',
      label: 'FAX番号',
      rows: 1,
    },
    {
      key: 'introduction',
      label: 'はじめに',
      minRows: 4,
    },
    {
      key: 'aboutUnitPrice',
      label: '単価について',
      rows: 1,
    },
    {
      key: 'paymentTerm',
      label: '支払条件',
      minRows: 4,
    },
    {
      key: 'freeTextOneTitle',
      label: 'フリーテキスト1 タイトル',
      rows: 1,
    },
    {
      key: 'freeTextOneContent',
      label: 'フリーテキスト1 内容',
      minRows: 3,
    },
    {
      key: 'freeTextTwoTitle',
      label: 'フリーテキスト2 タイトル',
      rows: 1,
    },
    {
      key: 'freeTextTwoContent',
      label: 'フリーテキスト2 内容',
      minRows: 3,
    },
    {
      key: 'freeTextThreeContent',
      label: 'フリーテキスト3 内容',
      minRows: 3,
    },
    {
      key: 'freeTextFourContent',
      label: 'フリーテキスト4 内容',
      minRows: 3,
    },
  ];

  const flashIntroduction = useFlash(debouncedState.introduction);
  const flashAboutUnitPrice = useFlash(debouncedState.aboutUnitPrice);
  const flashPaymentTerm = useFlash(debouncedState.paymentTerm);
  const flashCompanyName = useFlash(debouncedState.companyName);
  const flashCompanyAddress = useFlash(debouncedState.companyAddress);
  const flashCompanyPhoneNo = useFlash(debouncedState.companyPhoneNo);
  const flashCompanyFaxNo = useFlash(debouncedState.companyFaxNo);
  const flashFreeTextOneTitle = useFlash(debouncedState.freeTextOneTitle);
  const flashFreeTextOneContent = useFlash(debouncedState.freeTextOneContent);
  const flashFreeTextTwoTitle = useFlash(debouncedState.freeTextTwoTitle);
  const flashFreeTextTwoContent = useFlash(debouncedState.freeTextTwoContent);
  const flashFreeTextThreeContent = useFlash(debouncedState.freeTextThreeContent);
  const flashFreeTextFourContent = useFlash(debouncedState.freeTextFourContent);
  const flashImageFile = useFlash(debouncedState.imageFile);
  const flashBranchName = useFlash(debouncedState.branchName);
  const flashContactPerson = useFlash(debouncedState.contactPerson);

  useEffect(() => {
    setOutsourcingContractSettingChange((prev) => {
      const newValue = {
        flashIntroduction,
        flashAboutUnitPrice,
        flashPaymentTerm,
        flashCompanyName,
        flashCompanyAddress,
        flashCompanyPhoneNo,
        flashCompanyFaxNo,
        flashFreeTextOneTitle,
        flashFreeTextOneContent,
        flashFreeTextTwoTitle,
        flashFreeTextTwoContent,
        flashFreeTextThreeContent,
        flashFreeTextFourContent,
        flashImageFile,
        flashBranchName,
        flashContactPerson,
      };
      if (Object.values(newValue).every((it) => !it)) {
        return prev;
      }
      return newValue;
    });
  }, [flashAboutUnitPrice, flashCompanyAddress, flashCompanyFaxNo, flashCompanyName, flashCompanyPhoneNo, flashFreeTextFourContent, flashFreeTextOneContent, flashFreeTextOneTitle, flashFreeTextThreeContent, flashFreeTextTwoContent, flashFreeTextTwoTitle, flashIntroduction, flashPaymentTerm, flashImageFile, flashBranchName, flashContactPerson]);

  // 状態変更を検知してフラグを立てる
  useEffect(() => {
    setShouldGeneratePreview(true);
  }, [outsourcingContractSettingChange]);

  useEffect(() => {
    if (settingIsLoading) return;
    if (setting) return;

    const initializeSetting = async () => {
      await create.mutateAsync();
    };

    void initializeSetting();
  }, [setting, settingIsLoading]);

  // フラグが立った時のみプレビュー生成を実行
  useEffect(() => {
    if (!shouldGeneratePreview) return;

    const generatePreview = async () => {
      if (settingIsLoading) {
        setPreviewHtml('');
        return;
      }
      const entity: OutsourcingContractPdfPreviewRequestEntity = {
        outsourcingContractSettings: debouncedState,
        outsourcingContractSettingChange,
        preview: true,
      };

      const result = await createPreview.mutateAsync(entity);
      const imageUrl = (result.headers['x-image-url'] || result.headers['X-Image-Url']) as string;
      if (imageUrl) {
        dispatch({ type: 'SET_FIELD', key: 'imageUrl', value: imageUrl });
        dispatchDebounced({ type: 'SET_FIELD', key: 'imageUrl', value: imageUrl });
        dispatch({ type: 'SET_FIELD', key: 'imageFile', value: null });
        dispatchDebounced({ type: 'SET_FIELD', key: 'imageFile', value: null });
      }
      setPreviewHtml(String(result.data));
      if (!setting) {
        void queryClient.invalidateQueries({
          queryKey: [queryKey]
        });
      }
    };

    void generatePreview();
    setShouldGeneratePreview(false);
  }, [shouldGeneratePreview, debouncedState, outsourcingContractSettingChange, settingIsLoading, setting]);

  // iframeの高さを取得する関数
  const updateIframeHeight = useCallback(() => {
    let tallestHeight = 0;
    if (iframeRef.current) {
      const height = iframeRef.current.contentWindow?.document.body.scrollHeight;
      if (height) {
        tallestHeight = height;
      }
    }
    if (leftStackRef.current) {
      const height = leftStackRef.current.scrollHeight;
      if (height && tallestHeight < height) {
        tallestHeight = height;
      }
    }
    setContentHeight(tallestHeight + 32); // padding分を加算
  }, []);

  // プレビューHTMLが更新されたら高さを更新
  useEffect(() => {
    if (previewHtml) {
      // iframeの読み込み完了を待つ
      const timer = setTimeout(updateIframeHeight, 100);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [previewHtml, updateIframeHeight]);

  useEffect(() => {
    dispatchDebounced({ type: 'SET_FIELD', key: 'imageFile', value: selectedImage });
  }, [selectedImage]);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    setSelectedImage(acceptedFiles[0]);
  }, []);

  const { getRootProps, getInputProps } = useDropzone({ onDrop });

  const handleSave = () => {
    void update.mutateAsync(debouncedState, {
      onSuccess: () => {
        enqueueSnackbar('保存しました。', { variant: 'success' });
      },
      onError: () => {
        enqueueSnackbar('保存に失敗しました。', { variant: 'error' });
      }
    });
  };

  return (
    <>
      <title>委託表設定</title>
      <Box p={1} sx={{ height: 'calc(100vh - 64px)', overflow: 'auto' }}>
        <Paper>
          <Typography variant="h4" p={2}>委託表設定</Typography>
          <Stack spacing={2} direction="row" sx={{ width: '100%', p: 2 }} >
            {/* 左側: 入力フォーム */}
            <Stack sx={{ width: '50%' }} >
              <Paper sx={{ height: '100%', }}>
                <Stack spacing={2} sx={{ m: 0, p: 2, pb: 0, }} ref={leftStackRef}>
                  <Box
                    {...getRootProps()}
                    sx={{
                      width: '100%',
                      height: '100%',
                      color: 'rgba(110, 119, 129, 1)',
                      border: '5px dashed #E2E4E6',
                      borderRadius: '20px',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: 4
                    }}
                  >
                    <input {...getInputProps({
                        accept: 'image/jpeg,image/png,image/gif'
                      })}
                    />
                    <p>
                      {selectedImage ? selectedImage.name : 'ロゴ画像を選択またはドロップしてください'}
                    </p>
                  </Box>
                  {inputFields.map((field) => (
                    <DebouncedTextField
                      key={field.key}
                      label={field.label}
                      multiline={!(field.rows && field.rows === 1)}
                      size="small"
                      rows={field.rows}
                      minRows={field.minRows}
                      value={stringUtil.toText(formState[field.key])}
                      keyValue={field.key}
                      onValueChange={(val) => dispatch({ type: 'SET_FIELD', key: field.key, value: val })}
                      onDebouncedChange={(val) => dispatchDebounced({ type: 'SET_FIELD', key: field.key, value: val })}
                    />
                  ))}
                  <Box display="flex" justifyContent="flex-end" sx={{ px: 0, pb: 2 }}>
                    <Button variant="contained" color="primary" onClick={handleSave}>
                      保存
                    </Button>
                  </Box>
                </Stack>
              </Paper>
            </Stack>
            {/* 右側: プレビュー */}
            <Stack sx={{ width: '50%' }} >
              <Paper sx={{ p: 2, height: '100%', }}>
                <iframe
                  ref={iframeRef}
                  srcDoc={previewHtml}
                  style={{ width: '100%', height: '100%', border: 'none' }}
                  title="preview"
                />
              </Paper>
            </Stack>
          </Stack>
        </Paper>
      </Box>
    </>
  );
});

export default OutsourcingContractSettingsPresenter;
